/**
 * Console/PoC helpers for wholesale PDF preview (no form submit, no Cloudinary in test).
 *
 * Usage:
 *   await AkPdf.test()
 */
(function () {
  'use strict';

  function getForm(form) {
    return form || document.querySelector('form.ak-wholesale-order__contact-form');
  }

  function readContactFields(form) {
    var fields = {};
    var elements = form.elements;
    var i;
    var el;
    var match;
    var nameRe = /^contact\[(.+)\]$/;

    for (i = 0; i < elements.length; i += 1) {
      el = elements[i];
      if (!el.name) continue;
      match = el.name.match(nameRe);
      if (!match) continue;
      if ((el.type === 'checkbox' || el.type === 'radio') && !el.checked) continue;
      fields[match[1]] = el.value || '';
    }

    return fields;
  }

  function buildSummary(form) {
    var fields = readContactFields(form);
    var quantitiesText = fields['Order Quantities'] || '';
    if (!quantitiesText && fields.Qty) {
      quantitiesText = 'Qty: ' + fields.Qty;
    }
    var files = [];

    if (fields['Logo File URL'] || fields['Logo File Name']) {
      files.push({
        label: 'Logo',
        name: fields['Logo File Name'] || '',
        url: fields['Logo File URL'] || ''
      });
    }

    if (fields['Design Reference URL'] || fields['Design Reference File Name']) {
      files.push({
        label: 'Design Reference',
        name: fields['Design Reference File Name'] || '',
        url: fields['Design Reference URL'] || ''
      });
    }

    return {
      customer: {
        name: fields.name || '',
        email: fields.email || '',
        phone: fields.phone || fields.Phone || ''
      },
      project: {
        product: fields.Product || '',
        sku: fields.SKU || '',
        color: fields['Selected Color'] || '',
        paymentMethod: fields['Payment Method'] || '',
        shippingMethod: fields['Shipping Method'] || '',
        quantitiesText: quantitiesText,
        orderType: fields['Order Type'] || ''
      },
      customization: {
        types: fields['Type of Customization'] || '',
        other: fields['Customization Other'] || '',
        designDescription: fields['Describe Your Design'] || ''
      },
      notes: {
        specialInstructions: fields['Special Instructions'] || ''
      },
      uploadedFiles: files,
      submittedAt: new Date().toISOString()
    };
  }

  function escapeHtml(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /**
   * "S: 2, M: 1" → "S [2], M [1]"
   * @param {string} quantitiesText
   * @returns {string}
   */
  function formatSizeQuantities(quantitiesText) {
    if (!quantitiesText) return '';

    return quantitiesText
      .split(',')
      .map(function (part) {
        part = part.trim();
        if (!part) return '';
        var sep = part.indexOf(':');
        if (sep === -1) return part;
        var size = part.slice(0, sep).trim();
        var qty = part.slice(sep + 1).trim();
        return size + ' [' + qty + ']';
      })
      .filter(Boolean)
      .join(', ');
  }

  /**
   * @param {object} project
   * @returns {string}
   */
  function formatItemCode(project) {
    project = project || {};
    if (project.sku) return project.sku;
    var title = project.product || '';
    if (title && project.color) return title + ' [' + project.color + ']';
    if (project.color) return '[' + project.color + ']';
    return title;
  }

  function metaCell(label, value) {
    if (!label) {
      return '<td style="vertical-align:top;padding:2px 10px 6px 0;width:33%;"></td>';
    }
    return (
      '<td style="vertical-align:top;padding:2px 10px 6px 0;width:33%;">' +
      '<div style="font-size:12px;line-height:1.4;color:#000;">' +
      '<strong>' +
      escapeHtml(label) +
      ':</strong> ' +
      escapeHtml(value == null ? '' : String(value)) +
      '</div>' +
      '</td>'
    );
  }

  function metaRow(cells) {
    return '<tr>' + cells.join('') + '</tr>';
  }

  function renderHtml(summary) {
    var p = summary.project || {};
    var c = summary.customer || {};
    var n = summary.notes || {};
    var custom = summary.customization || {};
    var orderDate = summary.submittedAt
      ? new Date(summary.submittedAt).toLocaleString()
      : '';
    var sizeQty = formatSizeQuantities(p.quantitiesText || '');
    var itemCode = formatItemCode(p);

    var metaRows =
      metaRow([
        metaCell('Order Number', ''),
        metaCell('Customer PO', c.name || ''),
        metaCell('Order Date', orderDate)
      ]) +
      metaRow([
        metaCell('Shipping Method', p.shippingMethod || ''),
        metaCell('Ship To', c.name || ''),
        metaCell('Payment Terms', p.paymentMethod || '')
      ]) +
      metaRow([
        metaCell('Email', c.email || ''),
        metaCell('CusNo', ''),
        metaCell('', '')
      ]);

    if (p.orderType) {
      metaRows += metaRow([
        metaCell('Order Type', p.orderType),
        metaCell('', ''),
        metaCell('', '')
      ]);
    }

    var lineRow =
      '<tr>' +
      '<td style="border:1px solid #000;padding:6px 8px;vertical-align:top;">' +
      escapeHtml(itemCode) +
      '</td>' +
      '<td style="border:1px solid #000;padding:6px 8px;vertical-align:top;">' +
      escapeHtml(sizeQty) +
      '</td>' +
      '<td style="border:1px solid #000;padding:6px 8px;vertical-align:top;">' +
      escapeHtml(p.shippingMethod || '') +
      '</td>' +
      '<td style="border:1px solid #000;padding:6px 8px;vertical-align:top;text-align:center;"></td>' +
      '</tr>';

    var belowTable = '';
    if (n.specialInstructions && String(n.specialInstructions).trim()) {
      belowTable +=
        '<div style="margin:16px 0 0;font-size:12px;color:#000;">' +
        '<strong>Special Instructions:</strong> ' +
        escapeHtml(n.specialInstructions) +
        '</div>';
    }

    var extras = '';
    var hasCustom =
      (custom.types && custom.types.trim()) ||
      (custom.other && custom.other.trim()) ||
      (custom.designDescription && custom.designDescription.trim());

    if (hasCustom) {
      extras +=
        '<div style="margin:16px 0 0;font-size:12px;color:#000;">' +
        '<div style="margin:0 0 4px;"><strong>Type of Customization:</strong> ' +
        escapeHtml(custom.types || '') +
        '</div>' +
        '<div style="margin:0 0 4px;"><strong>Customization Other:</strong> ' +
        escapeHtml(custom.other || '') +
        '</div>' +
        '<div style="margin:0 0 4px;"><strong>Describe Your Design:</strong> ' +
        escapeHtml(custom.designDescription || '') +
        '</div>' +
        '</div>';
    }

    if (summary.uploadedFiles && summary.uploadedFiles.length) {
      extras +=
        '<div style="margin:12px 0 0;font-size:12px;color:#000;">' +
        summary.uploadedFiles
          .map(function (file) {
            return (
              '<div style="margin:0 0 4px;"><strong>' +
              escapeHtml(file.label) +
              (file.name ? ' (' + escapeHtml(file.name) + ')' : '') +
              ':</strong> ' +
              escapeHtml(file.url || '') +
              '</div>'
            );
          })
          .join('') +
        '</div>';
    }

    return (
      '<div id="ak-pdf-sheet" style="box-sizing:border-box;width:650px;max-width:650px;padding:16px;background:#ffffff;color:#000000;font-family:Arial,Helvetica,sans-serif;font-size:11px;line-height:1.35;">' +
      '<h1 style="text-align:center;font-size:16px;font-weight:700;margin:0 0 14px;color:#000;">Online Order Details</h1>' +
      '<table style="width:100%;border-collapse:collapse;margin:0 0 14px;table-layout:fixed;">' +
      '<tbody>' +
      metaRows +
      '</tbody>' +
      '</table>' +
      '<table style="width:100%;border-collapse:collapse;border:1px solid #000;table-layout:fixed;">' +
      '<thead>' +
      '<tr>' +
      '<th style="border:1px solid #000;padding:5px 6px;text-align:left;font-weight:700;text-decoration:underline;width:28%;">Item</th>' +
      '<th style="border:1px solid #000;padding:5px 6px;text-align:left;font-weight:700;text-decoration:underline;width:36%;">Size[Quantity]</th>' +
      '<th style="border:1px solid #000;padding:5px 6px;text-align:left;font-weight:700;text-decoration:underline;width:22%;">ShipVia</th>' +
      '<th style="border:1px solid #000;padding:5px 6px;text-align:left;font-weight:700;text-decoration:underline;width:14%;">Back Order</th>' +
      '</tr>' +
      '</thead>' +
      '<tbody>' +
      lineRow +
      '</tbody>' +
      '</table>' +
      belowTable +
      extras +
      '<div style="margin:24px 0 0;text-align:center;font-size:10px;color:#333;">' +
      '© AKWA by Pacico, Inc. All Rights Reserved.' +
      '</div>' +
      '</div>'
    );
  }

  function asPdfBlob(blob) {
    if (blob && blob.type === 'application/pdf') return blob;
    return new Blob([blob], { type: 'application/pdf' });
  }

  function wait(ms) {
    return new Promise(function (resolve) {
      setTimeout(resolve, ms);
    });
  }

  function resetCloneStyles(root) {
    if (!root) return;
    root.style.setProperty('color', '#000000', 'important');
    root.style.setProperty('background', '#ffffff', 'important');
    root.style.setProperty('opacity', '1', 'important');
    root.style.setProperty('visibility', 'visible', 'important');
    root.style.setProperty('transform', 'none', 'important');
    root.style.setProperty('filter', 'none', 'important');

    var nodes = root.querySelectorAll('*');
    var i;
    for (i = 0; i < nodes.length; i += 1) {
      nodes[i].style.setProperty('color', '#000000', 'important');
      nodes[i].style.setProperty('opacity', '1', 'important');
      nodes[i].style.setProperty('visibility', 'visible', 'important');
      nodes[i].style.setProperty('transform', 'none', 'important');
      nodes[i].style.setProperty('filter', 'none', 'important');
      nodes[i].style.setProperty('-webkit-text-fill-color', '#000000', 'important');
    }
  }

  /**
   * @param {string} html
   * @returns {Promise<Blob>}
   */
  function generatePdfBlob(html) {
    if (typeof window.html2pdf !== 'function') {
      return Promise.reject(new Error('html2pdf.js is not loaded'));
    }

    var prevScrollX = window.scrollX;
    var prevScrollY = window.scrollY;
    window.scrollTo(0, 0);

    // Ancestor transforms (theme sticky/header) cause html2canvas left-crop
    var restoreTransforms = [];
    var ancestor = document.body;
    while (ancestor) {
      restoreTransforms.push({
        el: ancestor,
        transform: ancestor.style.transform,
        webkitTransform: ancestor.style.webkitTransform,
        filter: ancestor.style.filter
      });
      ancestor.style.setProperty('transform', 'none', 'important');
      ancestor.style.setProperty('-webkit-transform', 'none', 'important');
      ancestor.style.setProperty('filter', 'none', 'important');
      ancestor = ancestor.parentElement;
    }

    var container = document.createElement('div');
    container.setAttribute('data-ak-pdf-render-root', '');
    container.style.cssText =
      'position:absolute;left:0;top:0;width:650px;margin:0;padding:0;' +
      'background:#ffffff;z-index:2147483646;';
    container.innerHTML = html;
    document.body.insertBefore(container, document.body.firstChild);

    var el = container.querySelector('#ak-pdf-sheet') || container;

    function cleanup() {
      if (container.parentNode) container.parentNode.removeChild(container);
      restoreTransforms.forEach(function (item) {
        item.el.style.transform = item.transform;
        item.el.style.webkitTransform = item.webkitTransform;
        item.el.style.filter = item.filter;
      });
      window.scrollTo(prevScrollX, prevScrollY);
    }

    return wait(300)
      .then(function () {
        console.log(
          '[AkPdf] render target',
          el.offsetWidth,
          el.offsetHeight,
          (el.innerText || '').slice(0, 80)
        );

        if (!el.offsetWidth || !el.offsetHeight) {
          throw new Error('PDF source element has no dimensions');
        }

        var w = el.offsetWidth;
        var h = el.offsetHeight;

        var opt = {
          margin: [12, 12, 12, 12],
          filename: 'order-details-' + Date.now() + '.pdf',
          image: { type: 'jpeg', quality: 0.98 },
          enableLinks: false,
          pagebreak: { mode: [] },
          html2canvas: {
            scale: 1.5,
            useCORS: true,
            allowTaint: true,
            backgroundColor: '#ffffff',
            logging: false,
            letterRendering: true,
            x: 0,
            y: 0,
            scrollX: 0,
            scrollY: 0,
            width: w,
            height: h,
            windowWidth: w,
            windowHeight: h,
            onclone: function (clonedDoc) {
              var clonedRoot = clonedDoc.querySelector('[data-ak-pdf-render-root]');
              var clonedSheet = clonedDoc.getElementById('ak-pdf-sheet');
              if (clonedRoot) {
                clonedRoot.style.position = 'static';
                clonedRoot.style.left = 'auto';
                clonedRoot.style.top = 'auto';
                clonedRoot.style.transform = 'none';
              }
              resetCloneStyles(clonedSheet || clonedRoot);
              var n = clonedDoc.body;
              while (n) {
                n.style.setProperty('transform', 'none', 'important');
                n.style.setProperty('filter', 'none', 'important');
                n = n.parentElement;
              }
            }
          },
          jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
        };

        return window.html2pdf().set(opt).from(el).outputPdf('blob');
      })
      .then(function (blob) {
        cleanup();
        var pdfBlob = asPdfBlob(blob);
        console.log('[AkPdf] blob', pdfBlob.size, pdfBlob.type);
        if (pdfBlob.size < 1500) {
          console.warn('[AkPdf] blob looks too small — capture may have failed');
        }
        return pdfBlob;
      })
      .catch(function (error) {
        cleanup();
        throw error;
      });
  }

  /**
   * Open a PDF Blob in a new tab (forces application/pdf MIME).
   * @param {Blob} blob
   * @returns {string}
   */
  function previewBlob(blob) {
    var pdfBlob = asPdfBlob(blob);
    var url = URL.createObjectURL(pdfBlob);
    var win = window.open('', '_blank');

    if (!win) {
      console.warn('[AkPdf] popup blocked — open manually:', url);
      return url;
    }

    win.document.open();
    win.document.write(
      '<!doctype html><title>PDF Preview</title>' +
        '<style>html,body{margin:0;height:100%;background:#525659}</style>' +
        '<embed src="' +
        url +
        '" type="application/pdf" width="100%" height="100%">'
    );
    win.document.close();
    console.log('[AkPdf] preview opened', url);
    return url;
  }

  function uploadPdf(blob) {
    var cfg = window.akCloudinaryConfig || {};
    var cloudName = cfg.cloudName;
    var uploadPreset = cfg.uploadPreset;
    var folder = cfg.folder || 'customer-uploads';

    if (!cloudName || !uploadPreset) {
      return Promise.reject(new Error('Missing akCloudinaryConfig cloudName/uploadPreset'));
    }

    var formData = new FormData();
    formData.append('file', asPdfBlob(blob), 'production-sheet-' + Date.now() + '.pdf');
    formData.append('upload_preset', uploadPreset);
    if (folder) formData.append('folder', folder);

    return fetch('https://api.cloudinary.com/v1_1/' + cloudName + '/auto/upload', {
      method: 'POST',
      body: formData
    }).then(function (response) {
      return response.text().then(function (text) {
        var body = null;
        try {
          body = JSON.parse(text);
        } catch (e) {
          body = null;
        }

        if (!response.ok) {
          throw new Error(
            (body && body.error && body.error.message) ||
              'Upload failed (' + response.status + ')'
          );
        }

        if (!body || !body.secure_url) {
          throw new Error('Upload failed: missing secure_url');
        }

        return {
          secureUrl: body.secure_url,
          publicId: body.public_id,
          raw: body
        };
      });
    });
  }

  /**
   * Open the HTML summary in a new tab (no PDF) — useful to verify layout/data.
   * @param {HTMLFormElement} [form]
   * @returns {{ summary: object, previewUrl: string }}
   */
  function previewHtml(form) {
    form = getForm(form);
    if (!form) throw new Error('Wholesale contact form not found');

    var summary = buildSummary(form);
    var html = renderHtml(summary);
    var win = window.open('', '_blank');
    if (!win) throw new Error('Popup blocked');

    win.document.open();
    win.document.write(
      '<!doctype html><title>Order Details HTML</title>' +
        '<body style="margin:0;background:#fff;">' +
        html +
        '</body>'
    );
    win.document.close();

    console.log('[AkPdf] HTML preview opened', summary);
    return { summary: summary, html: html };
  }

  /**
   * Build PDF from wholesale form and open preview only (no Cloudinary upload).
   * @param {HTMLFormElement} [form]
   * @returns {Promise<{ summary: object, blob: Blob, previewUrl: string }>}
   */
  function test(form) {
    form = getForm(form);
    if (!form) {
      return Promise.reject(new Error('Wholesale contact form not found'));
    }

    console.log('[AkPdf.test] reading form…');
    var summary = buildSummary(form);
    console.table(summary.customer);
    console.table(summary.project);
    console.log('[AkPdf.test] summary', summary);

    console.log('[AkPdf.test] generating PDF…');
    return generatePdfBlob(renderHtml(summary)).then(function (blob) {
      var previewUrl = previewBlob(blob);
      return {
        summary: summary,
        blob: blob,
        previewUrl: previewUrl
      };
    });
  }

  window.AkPdf = {
    test: test,
    previewHtml: previewHtml,
    buildSummary: buildSummary,
    renderHtml: renderHtml,
    generatePdfBlob: generatePdfBlob,
    previewBlob: previewBlob,
    uploadPdf: uploadPdf
  };

  console.log('[AkPdf] ready — run: await AkPdf.test()  or  AkPdf.previewHtml()');
})();
